<template>
  <div class="summary-bar d-flex align-center flex-wrap mt-3 mb-3">
    <div class="summary-pill d-flex align-center">
      <v-icon color="primary" small class="mr-2">mdi-calendar</v-icon>
      <span class="font-weight-bold">{{ formatDate(date) }}</span>
    </div>
    <div class="summary-pill summary-stat">
      <span class="font-weight-bold">{{ summary.count }}</span>
      <span class="grey--text summary-label">{{ $t("orders") }}</span>
    </div>
    <div class="summary-pill summary-stat">
      <span class="font-weight-bold">
        <template v-if="revenueParts.length">
          <span v-for="(part, i) in revenueParts" :key="part.currency">
            <span v-if="i > 0"> + </span>{{ part.amount.toFixed(2) }} {{ part.currency }}
          </span>
        </template>
        <template v-else>0.00 {{ currency }}</template>
      </span>
      <span class="grey--text summary-label">{{ $t("total") }}</span>
    </div>
    <div class="d-flex flex-wrap summary-breakdown">
      <v-chip
        small
        :style="allChipStyle"
        class="font-weight-medium mr-2"
        @click="selectStatus(null)"
      >
        {{ summary.count }} {{ $t("all") }}
      </v-chip>
      <v-chip
        v-for="b in summary.breakdown"
        :key="b.state"
        small
        :style="statusChipStyle(b.state, b.state === selectedStatus)"
        class="font-weight-medium mr-2"
        @click="selectStatus(b.state)"
      >
        <span class="status-dot mr-1" :style="statusDotStyle(b.state)"></span>
        {{ b.count }} {{ $t(b.state) }}
      </v-chip>
    </div>
  </div>
</template>

<script>
import hasGuestOrderStatus from "@/mixins/report/hasGuestOrderStatus";

export default {
  name: "ReportTableSummaryBar",
  mixins: [hasGuestOrderStatus],
  props: {
    date: {
      type: String,
      required: true,
    },
    dateData: {
      type: Array,
      default: () => [],
    },
    currency: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      selectedStatus: null,
    };
  },
  computed: {
    summary() {
      const counts = {};
      let revenue = 0;
      (this.dateData || []).forEach((item) => {
        revenue += this.calculateTotal(item);
        const order = item.orders_guest && item.orders_guest[0];
        if (order) {
          counts[order.state] = (counts[order.state] || 0) + 1;
        }
      });
      const breakdown = Object.entries(counts).map(([state, count]) => ({
        state,
        count,
      }));
      return { count: (this.dateData || []).length, revenue, breakdown };
    },
    // Revenue split by each order's restaurant currency, so mixed-currency
    // days (e.g. DIN + KM) are not summed into one meaningless number.
    revenueParts() {
      const buckets = {};
      (this.dateData || []).forEach((item) => {
        (item.orders_guest || []).forEach((order) => {
          const cur = order?.restaurant?.settings?.price || this.currency || "KM";
          const sub = (order.order_guest_transactions || []).reduce(
            (s, t) => s + t.price * t.quantity,
            0
          );
          buckets[cur] = (buckets[cur] || 0) + sub;
        });
      });
      return Object.entries(buckets).map(([currency, amount]) => ({
        currency,
        amount,
      }));
    },
    allChipStyle() {
      const colors = this.getStatusColors("all");
      const isSelected = this.selectedStatus === null;
      return {
        backgroundColor: colors.bg,
        color: colors.fg,
        cursor: "pointer",
        opacity: isSelected ? 1 : 0.5,
        border: isSelected ? `1px solid ${colors.fg}` : "1px solid transparent",
      };
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("sr-Latn", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    selectStatus(state) {
      this.selectedStatus = state;
      this.$emit("filter-change", state);
    },
    statusChipStyle(status, isSelected) {
      const colors = this.getStatusColors(status);
      const isFiltering = this.selectedStatus !== null;
      return {
        backgroundColor: colors.bg,
        color: colors.fg,
        cursor: "pointer",
        opacity: isFiltering && !isSelected ? 0.5 : 1,
        border: isSelected ? `1px solid ${colors.fg}` : "1px solid transparent",
      };
    },
  },
};
</script>

<style scoped>
.summary-bar {
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e1dfda;
  border-radius: 6px;
  gap: 10px;
}

.summary-pill {
  background: #f5f4f2;
  border: 1px solid #eae7e2;
  border-radius: 8px;
  padding: 6px 14px;
}

.summary-stat {
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
}

.summary-label {
  font-size: 12.5px;
}

.summary-breakdown {
  margin-left: auto;
}

.status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.summary-breakdown >>> .v-chip {
  transition: opacity 0.15s ease;
}
</style>
